import { EventEmitter } from 'eventemitter3';
import { AudioPlayer } from './AudioPlayer';
import { MediaPacketDefragmenter } from './MediaPacketDefragmenter';
import { OptimalDelayCalculator } from './OptimalDelayCalculator';
import { VideoPlayerWrapper } from './VideoPlayerWrapper';
export class MediaPlayer extends EventEmitter {
    optimalVideoDelayCalculator = new OptimalDelayCalculator();
    optimalVideoDelay;
    audioPlayer;
    mediaPacketDefragmenter;
    audioStartupRequested = false;
    audioRunning = false;
    videoPlayer;
    canvas;
    videoStarted = false;
    currentMediaStreamId;
    isProcessingMediaPacket = false;
    packetQueue = [];
    videoProperties;
    constructor() {
        super();
        this.videoPlayer = new VideoPlayerWrapper();
        this.audioPlayer = new AudioPlayer();
        this.audioPlayer.on('audioContextRunning', () => {
            this.audioStartupRequested = true;
            console.log('MediaPlayer::audioContextRunning()');
        });
        this.audioPlayer.on('newReferenceTimes', (presentationTimeMicros, realTimeMillis) => {
            this.videoPlayer.setReferenceTimes(presentationTimeMicros, realTimeMillis);
        });
        this.videoPlayer.on('decodedFrame', () => {
            this.optimalVideoDelayCalculator.addChunkArrivalTimeMillis(performance.now());
        });
        this.audioPlayer.on('audioContextStopped', () => {
            this.emit('audioMuted');
        });
        this.audioPlayer.on('audioContextRunning', () => {
            this.emit('audioUnmuted');
        });
        this.mediaPacketDefragmenter = new MediaPacketDefragmenter();
        this.mediaPacketDefragmenter.on('mediaPacket', async (packet) => {
            await this.queueMediaPacket(packet);
        });
    }
    async start(canvas) {
        this.canvas = canvas;
        await this.videoPlayer.init();
        await this.audioPlayer.init();
    }
    async unmute() {
        console.log('MediaPlayer::unmute()');
        await this.audioPlayer.unmute();
    }
    async mute() {
        console.log('MediaPlayer::mute()');
        this.audioRunning = false;
        await this.audioPlayer.mute();
    }
    feedMediaPacketFragment(packetFragment) {
        this.mediaPacketDefragmenter.feedMediaPacketFragment(packetFragment);
    }
    async reset() {
        console.error('MediaPlayer::reset()');
        this.audioStartupRequested = this.audioRunning;
        this.audioRunning = false;
        this.videoStarted = false;
        this.optimalVideoDelay = undefined;
        this.optimalVideoDelayCalculator.reset();
        await this.videoPlayer.reset();
        await this.audioPlayer.reset();
    }
    async queueMediaPacket(packet) {
        if (this.isProcessingMediaPacket) {
            this.packetQueue.push(packet);
            return;
        }
        this.isProcessingMediaPacket = true;
        try {
            // Process the current packet and then all queued packets in a loop
            let currentPacket = packet;
            while (currentPacket) {
                await this.feedMediaPacket(currentPacket);
                // Get the next packet from the queue (if any)
                currentPacket = this.packetQueue.shift();
            }
        }
        finally {
            this.isProcessingMediaPacket = false;
        }
    }
    feedMediaPacket = async (packet) => {
        if (this.currentMediaStreamId === undefined) {
            this.currentMediaStreamId = packet.mediaStreamId;
        }
        if (this.currentMediaStreamId !== undefined &&
            this.currentMediaStreamId !== packet.mediaStreamId) {
            this.currentMediaStreamId = packet.mediaStreamId;
            console.log('Media stream id changed, resetting media player', this.currentMediaStreamId, packet.mediaStreamId);
            await this.reset();
        }
        if (packet.videoFrames.length > 0) {
            const currentVideoFrame = packet.videoFrames[packet.videoFrames.length - 1];
            const newVideoProperties = getVideoPropertiesForVideoFrame(currentVideoFrame);
            if (!this.videoProperties ||
                !areVideoPropertiesEqual(this.videoProperties, newVideoProperties)) {
                this.videoProperties = newVideoProperties;
                this.emit('videoPropertiesChanged', newVideoProperties);
            }
            // update the optimal video delay
            this.optimalVideoDelay =
                this.optimalVideoDelayCalculator.calculateOptimalDelayMillis();
            // If we have the optimal video delay
            if (this.optimalVideoDelay) {
                // if the video has not started, start it
                if (!this.videoStarted) {
                    this.videoStarted = true;
                    this.startVideoPlayback(Number(currentVideoFrame.timestamp), this.optimalVideoDelay);
                }
                if (this.audioStartupRequested) {
                    this.audioStartupRequested = false;
                    this.startAudioPlaybackAtOptimalDelayMillis(this.optimalVideoDelay);
                }
            }
        }
        // feed the video player
        if (packet.videoFrames.length > 0) {
            await this.videoPlayer.feedVideoFrames(packet.videoFrames);
        }
        // feed the audio player
        if (packet.audioFrames.length > 0) {
            await this.audioPlayer.feedAudioFrames(packet.audioFrames);
        }
    };
    startVideoPlayback(firstFrameTimestampMicros, optimalVideoDelayMillis) {
        console.log('MediaPlayer::startVideoPlayback() with optimal delay', optimalVideoDelayMillis);
        const playbackDelayMicros = optimalVideoDelayMillis * 1000;
        const presentationTimeMicros = firstFrameTimestampMicros - playbackDelayMicros;
        if (this.canvas) {
            this.videoPlayer.start(presentationTimeMicros, this.canvas);
        }
    }
    startAudioPlaybackAtOptimalDelayMillis(optimalDelayMillis) {
        console.log('calling audioPlayer.startAtOptimalDelayMillis() with optimal delay', optimalDelayMillis);
        this.audioPlayer.startAtOptimalDelayMillis(optimalDelayMillis);
        this.audioRunning = true;
    }
}
function getVideoPropertiesForVideoFrame(frame) {
    return {
        width: frame.codedWidth,
        height: frame.codedHeight,
        type: frame.isScreenSharing ? 'screen' : 'camera',
        rotation: frame.rotation,
    };
}
function areVideoPropertiesEqual(a, b) {
    return (a.width === b.width &&
        a.height === b.height &&
        a.type === b.type &&
        a.rotation === b.rotation);
}
